import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatTimeIST, formatDateIST } from '../../utils/dateUtils';
import { formatCurrency, formatMilli } from '../../utils/formatUtils';
import { 
  ArrowDownRight, ArrowUpRight, Gift, Calendar, Download, 
  RefreshCw, FileSpreadsheet, CheckCircle, Clock, Loader2
} from 'lucide-react';
import { BackButton, PageSkeleton, StatusBadge } from '../../components/common';

export const AdminDailyReportPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [activeTab, setActiveTab] = useState('payments');
  const [markingVoucher, setMarkingVoucher] = useState(null);
  const [voucherAction, setVoucherAction] = useState('');

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    setSelectedDate(formatLocalDate(new Date()));
  }, []);

  const fetchReport = useCallback(async () => {
    if (!selectedDate) return;
    
    setLoading(true);
    try {
      const response = await adminAPI.getDailyReport(selectedDate);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load daily report');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      fetchReport();
    }
  }, [fetchReport, selectedDate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const downloadCSV = async (type) => {
    try {
      const response = await adminAPI.downloadDailyReportCSV(selectedDate, type);
      const { csv_content, filename } = response.data;
      
      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('CSV downloaded!');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const handleMarkVoucher = async () => {
    if (!markingVoucher) return;
    
    try {
      await adminAPI.updateVoucherStatus(markingVoucher.voucher_id, voucherAction);
      toast.success(`Voucher marked as ${voucherAction}`);
      setMarkingVoucher(null);
      fetchReport();
    } catch (error) {
      toast.error('Failed to update voucher status');
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-daily-report-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">Daily Report</h1>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Date Selector */}
        <Card className="mb-6 border-gold/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Calendar className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Select Date</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-9"
                />
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setSelectedDate(formatLocalDate(new Date()))}
              >
                Today
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {report && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <ArrowDownRight className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Payments</p>
                <p className="font-bold text-xl text-green-600">{report.summary.payments_count}</p>
                <p className="text-sm text-green-700">{formatCurrency(report.summary.payments_total)}</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 text-center">
                <ArrowUpRight className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Refunds</p>
                <p className="font-bold text-xl text-orange-600">{report.summary.refunds_count}</p>
                <p className="text-sm text-orange-700">{formatCurrency(report.summary.refunds_total)}</p>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4 text-center">
                <Gift className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Redemptions</p>
                <p className="font-bold text-xl text-purple-600">{report.summary.redemptions_count}</p>
                <p className="text-sm text-purple-700">{report.summary.redemptions_millis?.toFixed(2)} Milli's</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="refunds">Refunds</TabsTrigger>
            <TabsTrigger value="redemptions">Redemptions</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : (
            <>
              {/* Payments Tab */}
              <TabsContent value="payments">
                <div className="flex justify-end mb-4">
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('payments')}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
                
                {report?.payments?.length === 0 ? (
                  <Card className="border-gold/20">
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No payments on this date
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {report?.payments?.map((payment) => (
                      <Card key={payment.transaction_id} className="border-green-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-navy">{payment.customer_name}</p>
                              <p className="text-xs text-muted-foreground">{payment.phone}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatTimeIST(payment.created_at)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{formatCurrency(payment.amount_inr)}</p>
                              <p className="text-sm text-muted-foreground">{payment.milli_amount?.toFixed(2)} Milli's</p>
                              <p className="text-xs text-muted-foreground">@ {formatCurrency(payment.rate)}/M</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {/* Total */}
                    <Card className="border-green-400 bg-green-100">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-green-800">Total Payments</p>
                          <div className="text-right">
                            <p className="font-bold text-xl text-green-700">{formatCurrency(report?.summary?.payments_total)}</p>
                            <p className="text-sm text-green-600">{report?.summary?.payments_millis?.toFixed(2)} Milli's</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>

              {/* Refunds Tab */}
              <TabsContent value="refunds">
                <div className="flex justify-end mb-4">
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('refunds')}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
                
                {report?.refunds?.length === 0 ? (
                  <Card className="border-gold/20">
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No refunds on this date
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {report?.refunds?.map((refund) => (
                      <Card key={refund.transaction_id} className="border-orange-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-navy">{refund.customer_name}</p>
                              <p className="text-xs text-muted-foreground">{refund.phone}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatTimeIST(refund.created_at)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-orange-600">{formatCurrency(refund.amount_inr)}</p>
                              <p className="text-sm text-muted-foreground">{refund.milli_amount?.toFixed(2)} Milli's</p>
                              <div className="text-xs mt-1">
                                <span className="text-muted-foreground">Cost: {formatCurrency(refund.original_cost)}</span>
                                <Badge className={`ml-2 ${refund.profit >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {refund.profit >= 0 ? '+' : ''}{formatCurrency(refund.profit)}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {/* Total */}
                    <Card className="border-orange-400 bg-orange-100">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-orange-800">Total Refunds</p>
                            <p className="text-sm text-orange-600">Net P&L: {formatCurrency(report?.summary?.refunds_profit)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-xl text-orange-700">{formatCurrency(report?.summary?.refunds_total)}</p>
                            <p className="text-sm text-orange-600">{report?.summary?.refunds_millis?.toFixed(2)} Milli's</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>

              {/* Redemptions Tab */}
              <TabsContent value="redemptions">
                <div className="flex justify-end mb-4">
                  <Button variant="outline" size="sm" onClick={() => downloadCSV('redemptions')}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
                
                {report?.redemptions?.length === 0 ? (
                  <Card className="border-gold/20">
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No redemptions on this date
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {report?.redemptions?.map((redemption) => (
                      <Card key={redemption.voucher_id} className="border-purple-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-navy">{redemption.customer_name}</p>
                              <p className="text-xs text-muted-foreground">{redemption.phone}</p>
                              <p className="text-xs font-mono text-purple-600 mt-1">
                                Voucher: {redemption.voucher_id}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Expires: {formatDateIST(redemption.expires_at)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-purple-600">{redemption.milli_amount?.toFixed(2)} Milli's</p>
                              <p className="text-sm text-muted-foreground">{redemption.gram_value?.toFixed(3)}g</p>
                              <div className="mt-2">
                                {redemption.redeemed_at_store ? (
                                  <Badge className="bg-green-100 text-green-700">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Redeemed
                                  </Badge>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setMarkingVoucher(redemption);
                                      setVoucherAction('redeemed');
                                    }}
                                  >
                                    <Clock className="w-3 h-3 mr-1" />
                                    Mark Redeemed
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {/* Total */}
                    <Card className="border-purple-400 bg-purple-100">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-purple-800">Total Redemptions</p>
                          <div className="text-right">
                            <p className="font-bold text-xl text-purple-700">{report?.summary?.redemptions_millis?.toFixed(2)} Milli's</p>
                            <p className="text-sm text-purple-600">{report?.summary?.redemptions_grams?.toFixed(3)}g Gold</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* Mark Voucher Dialog */}
      <Dialog open={!!markingVoucher} onOpenChange={() => setMarkingVoucher(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Voucher Status</DialogTitle>
            <DialogDescription>
              Mark voucher {markingVoucher?.voucher_id} as redeemed at store?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkingVoucher(null)}>
              Cancel
            </Button>
            <Button onClick={handleMarkVoucher} className="gold-gradient text-navy">
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Redeemed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDailyReportPage;
